import clsx from 'clsx'
import React from 'react'

import { ETF_COLUMN_NAMES, IETFTrackerResponse } from '@dl/declarations'

type ColumnKey = keyof typeof ETF_COLUMN_NAMES

export interface TableProps {
    data: Record<ColumnKey, string | number>[]
    etf: string
    caption?: string
    columns: ColumnKey[]
}

interface ArticleETFProps {
    data: IETFTrackerResponse[]
    columns: ColumnKey[]

    caption?: string
    pagination?: {
        from: number
        show: boolean;
        component: React.ReactNode;
    }
}

export const ArticleETF: React.FC<ArticleETFProps> = (props) => {
    const {
        data,
        columns,
        caption,
        pagination = {
            show: false,
            from: 0
        }
    } = props;

    return (
        <table className="w-full drop-shadow-lg">
            <caption className="rounded-t-2xl bg-blue-700 p-[9px_15px] text-left text-[15px] font-semibold leading-[150%] text-white lg:text-[20px]">
                {caption}
            </caption>
            <thead>
                <tr className="h-[30px] text-[11px] text-white lg:text-[14px]">
                    <th className="w-[8%] bg-primary-700 px-[10px] text-left font-semibold lg:px-[16px]" />
                    {columns.map((col, j) => (
                        <th
                            key={col}
                            className={clsx(
                                'bg-primary-700 px-[4px] font-semibold',
                                j === 0 ? 'w-[32%] text-left' : 'w-[20%] text-center',
                            )}
                        >
                            {ETF_COLUMN_NAMES?.[col]}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((row, i) => {
                    const isLastRow = i === data.length - 1
                    const showFooter = pagination?.show
                    return (
                        <tr
                            key={'ticker' in row ? `${row.ticker}-${i}` : i}
                            className={clsx(
                                'h-[50px] px-[16px] text-[14px] font-bold text-blue-900 dark:text-white lg:text-[16px]',
                                i % 2 !== 0 ? 'bg-blue-75/80 dark:bg-blue-700' : 'bg-white/80 dark:bg-[#0D1E3C]',
                            )}
                        >
                            <td
                                className={clsx(
                                    'px-[10px] text-left lg:px-[16px]',
                                    isLastRow && !showFooter && 'rounded-bl-xl',
                                )}
                            >
                                {pagination.from + i + 1}
                            </td>
                            {columns.map((col, j) => (
                                <td
                                    key={col}
                                    className={clsx(
                                        'px-[4px] text-[11px] lg:text-[12px]',
                                        j === 0 ? 'text-left' : 'text-center',
                                        isLastRow && !showFooter && j === columns.length - 1 && 'rounded-br-xl',
                                    )}
                                >
                                    {row?.[col as keyof typeof row] ?? '-'}
                                </td>
                            ))}
                        </tr>
                    )
                })}
            </tbody>

            {pagination?.show && (
                <tfoot>
                    <tr>
                        <td colSpan={5} className="rounded-b-xl bg-white px-[24px] py-[12px] dark:bg-primary-700">
                            <div className="flex justify-between text-[14px]">
                                <div className="relative flex items-center gap-x-3">
                                </div>
                                {pagination.component}
                            </div>
                        </td>
                    </tr>
                </tfoot>
            )}
        </table>
    )
}
